// The delivery progress bar's native `title` (2026-08-16) — per-item receive
// status for the collapsed row's Supplier-cell bar (`order-delivery-progress`
// in `OrderTableView.js`). Owner: "hovering over the delivery progress bar
// should show a tooltip with the items and who received them (who ticked the
// received box for each item)."
//
// Built from `preview_items` (via `orderPreviewItems` in `OrderTableView.js`),
// which already carries `description` + `is_received` for the collapsed
// row's green-name treatment, plus the two fields added alongside this
// feature — `received_date` and `received_by_name` — to
// `ORDER_PREVIEW_ITEM_FIELDS` (`server/helpers/orderPreview.js`). No extra
// fetch, no extra endpoint: the same batched item query every list endpoint
// already runs.
//
// 🕶️ HISTORICAL NULL, DELIBERATE: an item ticked received before
// `order_items.received_by` existed carries `received_by_name: null` with a
// real `received_date` — the line reads "received <date>" with no name for
// those, never "Unknown" and never blank.
//
// 🚨 CHILD-`title` SHADOW RULE: this string is meant to go on the BAR
// wrapper (`data-testid="order-delivery-progress"`), never on the Supplier
// `<td>` itself — the cell keeps its own `title={supplier_name}`. A `title`
// on a child wins over its parent's, which is exactly the point here: hover
// the bar for the received list, hover the name for the supplier.

import { formatDateCompact } from './dateFormat';
import { deliveryCountableItems } from './deliveryCountableItems';

/**
 * One line per item:
 *   `<description> — received by <name>, <date>`  (received, name known)
 *   `<description> — received <date>`              (received, no name — historical)
 *   `<description> — not yet received`
 *
 * `Boolean(item.is_received)`, NEVER `=== true` — SQLite/pg both return the
 * stored INTEGER 0/1 for this column, matching the same guard the collapsed
 * row's green-name treatment uses.
 *
 * @param {Array<{description?:string, is_received?:any, received_date?:string, received_by_name?:string}>} items
 * @returns {string} multi-line tooltip text, '' for an empty/missing list
 */
export function buildDeliveryProgressTooltip(items) {
  if (!Array.isArray(items)) return '';
  // ⚖️ GOODS ONLY (owner ruling 2026-09-19). This tooltip hangs off the DELIVERY
  // PROGRESS BAR and lists what the bar is counting, line by line, with who
  // received each. An adjustment row listed here would read *"Hire price
  // adjustment — not yet received"* — the tooltip inviting somebody to go and
  // tick a credit note, which is exactly the prompt the owner ruled out.
  // 🪤 THE EMPTY CHECK MOVED BELOW THE FILTER. A list of nothing but adjustment
  // rows must also produce `''`; testing the RAW length would have returned a
  // tooltip with no lines in it.
  const countable = deliveryCountableItems(items);
  if (countable.length === 0) return '';
  return countable
    .map((item) => {
      const name = item?.description || 'Item';
      if (Boolean(item?.is_received)) {
        const date = formatDateCompact(item.received_date);
        return item.received_by_name
          ? `${name} — received by ${item.received_by_name}, ${date}`
          : `${name} — received ${date}`;
      }
      // 🔀 PER-LINE "ORDERED" (2026-10-28): `is_ordered` rides on the preview
      // items. Absent (an older payload) → the line reads as before.
      if (item && item.is_ordered !== undefined && !Number(item.is_ordered)) {
        return `${name} — not yet ordered`;
      }
      if (item && Number(item.is_ordered) && (item.ordered_by_name || item.ordered_date)) {
        const when = item.ordered_date ? formatDateCompact(item.ordered_date) : '';
        const who = item.ordered_by_name ? `ordered by ${item.ordered_by_name}` : 'ordered';
        return `${name} — ${who}${when ? `, ${when}` : ''} · not yet received`;
      }
      return `${name} — not yet received`;
    })
    .join('\n');
}
